<template>
  <div v-if="step" class="step-datetime-input">
    <Datepicker v-model="date" :placeholder="step.title" />
    <TimeInput v-model="time" />
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { ref, watch } from "vue";
import { currentTenantTimezone } from "@/lib/helpers";

import Datepicker from "@/components/Seranova/Datepicker/Datepicker.vue";
import TimeInput from "@/components/Seranova/TimeInput/TimeInput.vue";
import { TimelineStep } from "@/typings/scheduler";

const emit = defineEmits(["update:modelValue"]);
const props = defineProps<{ step: TimelineStep; defaultDate?: string }>();
const date = ref(props.step?.date || props.defaultDate || "");
const time = ref(props.step?.time || "");

// Compute the ISO string the step was initialized from so we can detect
// whether a change is a real edit or just a mount-time reformat by
// Datepicker/TimeInput normalizing their display values.
// The date carries a year — parse it so the timestamp reflects the
// appointment's actual year, not the current one. Without the year token,
// moment infers "now", which throws editing a prior-year appointment into the
// future and trips the "cannot be in the future" guard. Both the 2-digit
// (dropdown/stored) and 4-digit (typed custom year) forms are accepted; moment
// picks whichever matches the date string.
const DATETIME_FORMATS = ["MM/DD/YY h:mm A", "MM/DD/YYYY h:mm A"];

const initialIso =
  props.step?.date && props.step?.time
    ? moment
        .tz(
          props.step.date + " " + props.step.time,
          DATETIME_FORMATS,
          currentTenantTimezone
        )
        .toISOString()
    : null;

watch(date, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emitInput();
  }
});
watch(time, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emitInput();
  }
});
const emitInput = () => {
  const fieldName = props.step?.editableFieldName;
  if (!date.value || !time.value) {
    if (initialIso !== null) {
      emit("update:modelValue", { [fieldName]: null });
    }
    return;
  }
  const timeStr = moment
    .tz(date.value + " " + time.value, DATETIME_FORMATS, currentTenantTimezone)
    .toISOString();
  if (timeStr === initialIso) return;
  emit("update:modelValue", { [fieldName]: timeStr });
};
</script>

<style scoped>
.step-datetime-input {
  display: contents;
}
</style>
